import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Lock, MapPin, ShieldCheck, TrendingUp, Building2, Sparkles } from "lucide-react";
import PublicNavbar from "../components/layout/PublicNavbar";
import Footer from "../components/layout/Footer";
import { Pill, Button } from "../components/common/Primitives";
import TrustBadge from "../components/marketplace/TrustBadge";
import VerifiedBadge from "../components/common/VerifiedBadge";
import MatchReasonRewriter from "../components/MatchReasonRewriter";
import DebtIntelligencePanel from "../components/debt/DebtIntelligencePanel";
import api, { formatApiError, API_BASE } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import Seo from "../components/Seo";

function formatINR(v) {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

export default function StartupDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiPitch, setAiPitch] = useState(null);

  useEffect(() => {
    api.get(`/marketplace/startup/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    // Only fetch AI pitch for logged-in users viewing an unlocked profile
    if (!user || !data || data.locked) return;
    api.get(`/ai/pitch-summary/${id}`).then((r) => setAiPitch(r.data)).catch(() => {});
  }, [user, data, id]);

  const express = async () => {
    try {
      await api.post("/investor/interest", { startup_id: id });
      toast.success("Interest recorded. Admin will review shortly.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="min-h-screen bg-transparent text-ink-muted flex items-center justify-center">Loading…</div>;
  if (!data) return <div className="min-h-screen bg-transparent text-ink-muted flex items-center justify-center">Startup not found.</div>;

  const locked = data.locked;
  const fp = data.funding || {};

  return (
    <div className="min-h-screen bg-transparent text-ink">
      <Seo
        title={`${data.startup_name || "Startup Profile"} | VenturaZ`}
        description={
          data.tagline ||
          `${data.startup_name || "This startup"} on VenturaZ — sector ${data.sector || "—"}, stage ${data.business_stage || "—"}. Explore the pitch, team and funding ask.`
        }
        path={`/startup/${id}`}
        type="profile"
      />
      <Toaster richColors position="top-right" />
      <PublicNavbar />
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12" data-testid="startup-detail-page">
        <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
          <div className="flex items-start gap-4">
            {data.has_logo ? (
              <div className="w-20 h-20 rounded-lg border border-white/10 bg-ink-surface/60 flex items-center justify-center overflow-hidden shrink-0" data-testid="startup-detail-logo">
                <img
                  src={`${API_BASE}/marketplace/startup/${id}/logo`}
                  alt={`${data.startup_name} logo`}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                />
              </div>
            ) : (
              <div className="w-20 h-20 rounded-lg border border-white/10 bg-ink-surface/40 flex items-center justify-center shrink-0" data-testid="startup-detail-logo-placeholder">
                <Building2 size={28} className="text-ink-muted/50" />
              </div>
            )}
            <div>
              <div className="overline mb-2">{data.sector} · {data.business_stage}</div>
              <h1 className={`font-display text-3xl lg:text-5xl text-ink ${locked ? "italic" : ""}`} data-testid="startup-detail-name">{data.startup_name}</h1>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                {data.verified && <Pill tone="gold"><ShieldCheck size={11} className="mr-1" /> Verified</Pill>}
                {data.trust_badge?.tier && (
                  <VerifiedBadge badge={data.trust_badge} size="md" testId="startup-detail-verified-badge" />
                )}
                {typeof data.match_score === "number" && (
                  <div className="match-badge px-2.5 py-1 text-[11px] font-mono flex items-center gap-1"><TrendingUp size={11} /> Match {data.match_score}</div>
                )}
                {!locked && data.trust_score !== null && data.trust_score !== undefined && (
                  <TrustBadge total={data.trust_score} label={data.trust_label} size="md" testIdPrefix="startup-detail-trust" />
                )}
                <div className="flex items-center gap-1 text-ink-muted text-sm"><MapPin size={13} /> {data.city}</div>
              </div>
            </div>
          </div>
          {user?.role === "investor" && (
            <div className="flex flex-col gap-2 items-end">
              <Button onClick={express} data-testid="detail-express-interest">Express Interest</Button>
              {!locked && (
                <Link
                  to={`/investor/startup-passport/${id}`}
                  className="text-xs font-mono uppercase tracking-wider text-gold hover:text-gold/80 transition-colors flex items-center gap-1"
                  data-testid="detail-view-passport"
                >
                  <ShieldCheck size={12} /> View Verified Passport →
                </Link>
              )}
            </div>
          )}
        </div>

        {locked && (
          <div className="border border-gold/40 bg-gold/[0.04] p-4 mb-8 flex items-center gap-3">
            <Lock className="text-gold" size={16} />
            <div className="text-sm text-ink-soft">Sensitive fields are locked. <Link to="/investor/join" className="text-gold underline">Complete investor approval</Link> to unlock full profile.</div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <MetricCard label="Funding Ask" value={locked ? "₹XX Cr" : formatINR(fp.amount_required)} locked={locked} />
          <MetricCard label="Type" value={fp.funding_type || data.funding_type || "—"} />
          <MetricCard label="Annual Revenue" value={locked ? "₹XX Cr" : formatINR(fp.annual_revenue)} locked={locked} />
          {!locked && (
            <>
              <MetricCard label="ARR" value={formatINR(fp.arr)} />
              <MetricCard label="Burn / month" value={formatINR(fp.burn_rate)} />
              <MetricCard label="Runway" value={fp.runway_months ? `${fp.runway_months} mo` : "—"} />
            </>
          )}
        </div>

        {!locked && data.match_reason && (
          <MatchReasonRewriter
            startupId={data.startup_id || id}
            initialReason={data.match_reason}
          />
        )}

        {data.one_line_pitch && (
          <div className="border border-white/10 bg-white/70 backdrop-blur p-6 mb-6">
            <div className="overline mb-2">Pitch</div>
            <div className="text-ink-soft leading-relaxed">{data.one_line_pitch}</div>
          </div>
        )}

        {aiPitch?.summary && (
          <div className="border border-gold/30 bg-white/70 backdrop-blur p-6 mb-6" data-testid="ai-pitch-summary">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="text-gold" size={14} />
              <div className="overline">AI · Analyst-drafted summary</div>
              {aiPitch.source === "cache" && <Pill tone="muted">Cached</Pill>}
            </div>
            <div className="text-ink-soft leading-relaxed whitespace-pre-wrap">{aiPitch.summary}</div>
          </div>
        )}

        {!locked && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Detail t="Problem" b={fp.problem_statement} />
            <Detail t="Solution" b={fp.solution} />
            <Detail t="Business Model" b={fp.business_model} />
            <Detail t="Revenue Model" b={fp.revenue_model} />
            <Detail t="Traction" b={fp.traction} />
            <Detail t="Competitive Advantage" b={fp.competitive_advantage} />
            <Detail t="Use of Funds" b={fp.use_of_funds} />
            <Detail t="Timeline" b={fp.timeline_for_raise} />
          </div>
        )}

        {/* Debt Intelligence — shown when the founder has flipped consent */}
        {!locked && user && (
          <div className="mt-8">
            <DebtIntelligencePanel startupId={data.startup_id || id} />
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

function MetricCard({ label, value, locked }) {
  return (
    <div className="border border-white/10 bg-white/70 backdrop-blur p-4">
      <div className="overline">{label}</div>
      <div className={`font-heading text-ink text-lg mt-1 ${locked ? "locked-blur" : ""}`}><Building2 size={14} className="inline mr-1 text-gold/50" />{value}</div>
    </div>
  );
}

function Detail({ t, b }) {
  if (!b) return null;
  return (
    <div className="border border-white/10 bg-white/70 backdrop-blur p-5">
      <div className="overline mb-2">{t}</div>
      <div className="text-ink-soft text-sm leading-relaxed">{b}</div>
    </div>
  );
}
